import { Injectable, OnDestroy, inject } from '@angular/core';
import { DOCUMENT } from '@angular/common';

/**
 * Keeps native scrolling smooth by making each frame cheap.
 *
 * The app scrolls natively everywhere — there is no scroll-hijacking library.
 * Native scroll runs on the compositor thread, so it survives a busy main
 * thread; a JS-driven smooth-scroll library would move scrolling onto the main
 * thread and couple it to render work. The trade-off of staying native is that
 * scroll is only as smooth as the paint cost of each frame, which is what this
 * service manages.
 *
 * It publishes two classes on <html> that `styles.scss` (NATIVE SCROLL
 * PERFORMANCE LAYER) keys off:
 *
 * 1. `.gpu-ok`       — device can afford `backdrop-filter`. Absent by default,
 *                      so a device that fails detection gets the cheap path.
 * 2. `.is-scrolling` — a scroll gesture is in flight; decorative animations and
 *                      hover transitions pause until it settles.
 */
@Injectable({ providedIn: 'root' })
export class PlatformPerformanceService implements OnDestroy {
  private readonly document = inject(DOCUMENT);

  /** Frames stay whole if the scroll listener does nothing but set a flag. */
  private scrolling = false;
  private scrollIdleTimer: ReturnType<typeof setTimeout> | null = null;
  private rafId: number | null = null;
  private started = false;

  /**
   * How long after the last scroll event the page is considered settled.
   * Long enough to span the gaps between events during a slow drag, short
   * enough that animations resume before the user notices they stopped.
   */
  private static readonly SCROLL_IDLE_MS = 120;

  private readonly onScroll = () => {
    if (this.scrolling) {
      this.resetIdleTimer();
      return;
    }
    this.scrolling = true;

    // Never touch the DOM inside the scroll event itself — writing a class
    // there forces style recalc mid-gesture, the exact jank we are removing.
    this.rafId = requestAnimationFrame(() => {
      this.document.documentElement.classList.add('is-scrolling');
      this.rafId = null;
    });
    this.resetIdleTimer();
  };

  init(): void {
    // Guard against SSR/prerender, where there is no window.
    const win = this.document.defaultView;
    if (!win || this.started) return;
    this.started = true;

    this.detectGpuCapability(win);

    // Passive: tells the browser we will never preventDefault, so it can start
    // scrolling without waiting for this handler to return. Without it, every
    // scroll event blocks the compositor on main-thread availability.
    // Capture: catches scroll from nested panes too, since scroll does not bubble.
    win.addEventListener('scroll', this.onScroll, { passive: true, capture: true });
  }

  /**
   * Decides whether this device can afford `backdrop-filter` during scroll.
   *
   * There is no direct "is the GPU fast" API, so this uses the signals that are
   * actually available and errs toward the cheap path — a wrongly-plain
   * background is a cosmetic loss, a wrongly-enabled blur costs frames on
   * exactly the devices least able to spare them.
   */
  private detectGpuCapability(win: Window): void {
    const root = this.document.documentElement;

    // Browsers that cannot do it at all: nothing to gate. `CSS` is not on the
    // DOM lib's Window type, so reach for it through an explicit cast.
    const css = (win as { CSS?: { supports?(p: string, v: string): boolean } }).CSS;
    const supported =
      css?.supports?.('backdrop-filter', 'blur(1px)') ||
      css?.supports?.('-webkit-backdrop-filter', 'blur(1px)');
    if (!supported) return;

    // Users who asked for less motion get the cheap, static path.
    if (win.matchMedia?.('(prefers-reduced-motion: reduce)').matches) return;

    // Data-saver mode signals the user wants less work done on their behalf.
    // `connection` is Chromium-only; absence is not a failure signal.
    const connection = (navigator as { connection?: { saveData?: boolean } }).connection;
    if (connection?.saveData) return;

    // Core count is a coarse proxy for device tier and the most portable one
    // available. 4 cores excludes low-end phones where blur is most damaging.
    const cores = navigator.hardwareConcurrency ?? 0;
    if (cores > 0 && cores < 4) return;

    // `deviceMemory` is Chromium-only and reported in GiB, capped at 8.
    const memory = (navigator as { deviceMemory?: number }).deviceMemory;
    if (memory !== undefined && memory < 4) return;

    root.classList.add('gpu-ok');
  }

  private resetIdleTimer(): void {
    if (this.scrollIdleTimer) clearTimeout(this.scrollIdleTimer);

    this.scrollIdleTimer = setTimeout(() => {
      this.scrolling = false;
      this.scrollIdleTimer = null;
      this.document.documentElement.classList.remove('is-scrolling');
    }, PlatformPerformanceService.SCROLL_IDLE_MS);
  }

  ngOnDestroy(): void {
    const win = this.document.defaultView;
    win?.removeEventListener('scroll', this.onScroll, { capture: true });

    if (this.scrollIdleTimer) clearTimeout(this.scrollIdleTimer);
    if (this.rafId !== null) cancelAnimationFrame(this.rafId);
  }
}
